import React, {ReactNode} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../PortalDocument";
import {
	Tree,
	TreeCheckboxSelectionKeys,
	TreeEventNodeEvent,
	TreeExpandedKeysType,
	TreeMultipleSelectionKeys
} from "primereact/tree";
import {pdfjs} from "react-pdf";
import {Button} from "primereact/button";
import PortalDocumentManager from "../../PortalDocumentManager";
import Parameter from "../../../dialog/parameter/Parameter";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import DialogDefault from "../../../dialog/parameter/PortalDialogDefault";
import PortalDialogType from "../../../dialog/PortalDialogType";
import DocumentSettings from "../../DocumentSettings";
import Observer from "../../../../shared/class/Observer";
import DestinationEvent from "../../../viewer/DestinationEvent";
import {OutlineAddDialogDefinition} from "../../../dialog/components/document/OutlineAddDialog";
import {PATH_SEPARATOR} from "../../../../shared/helper/FileHelper";
import Exception from "../../../../shared/exception/Exception";
import ErrorCode from "../../../../shared/exception/ErrorCode";
import PortalContext from "../../../PortalContext";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	BaseToolbox,
	ItemReference,
	Settings,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalToolboxOutlineOutline} from "../../../../shared/models/PortalOutlineType";
import Immutable from "../../../../shared/immutable/Immutable";
import PortalDialogSecurity, {Intention} from "../../../dialog/PortalDialogSecurity";
import TreeNode from "primereact/treenode";
import Translate from "../../../../shared/translation/Translate";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import {SidebarFunctionGlobals} from "./SidebarFunctions";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";

interface OutlineListProperties extends I18nGlobals, SidebarFunctionGlobals, DebugGlobals {
	visible: boolean;
	context: PortalContext;
	destinationObserver?: Observer<DestinationEvent>;
	portalDocument?: Immutable<PortalDocumentInterface>;
	signatureEditProfile: SignatureEditProfile
}

interface OutlineListStatus {
	outlines: Array<TreeNode>;
	selected?: string | TreeMultipleSelectionKeys | TreeCheckboxSelectionKeys | null;
	expanded: TreeExpandedKeysType;
}

export interface Outline {
	title: string;
	bold: boolean;
	italic: boolean;
	color: Uint8ClampedArray;
	dest: string | Array<any> | null;
	url: string | null;
	unsafeUrl: string | undefined;
	newWindow: boolean | undefined;
	count: number | undefined;
	items: any[];
}

export default class OutlineList extends PortalPureComponent<OutlineListProperties, OutlineListStatus> {
	private nodeCount: number;

	/**
	 * Outline list component constructor
	 */
	constructor(props: Readonly<OutlineListProperties>) {
		super(props);

		this.state = {
			outlines: [],
			expanded: {}
		};
		this.nodeCount = 1;

		this.handleSelect = this.handleSelect.bind(this);
		this.expandList = this.expandList.bind(this);
		this.collapseList = this.collapseList.bind(this);
		this.deleteOutline = this.deleteOutline.bind(this);
	}

	/**
	 * Build outline data for the tree list
	 */
	private async buildOutlineList(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		try {
			let pdfDocument: pdfjs.PDFDocumentProxy = await portalDocument.loadViewerData();
			let outlines: Array<Outline> | null = await pdfDocument.getOutline();
			let result: Array<TreeNode> = [];

			if (outlines === null) {
				this.setState({
					outlines: result
				});
				return;
			}

			this.nodeCount = 1;
			for (let outline of outlines) {
				result.push(await this.buildTreeNode(outline, ""));
				this.nodeCount++;
			}

			this.setState({
				outlines: result
			});
		} catch (e: any) {
			// can be ignored here
		}
	}

	private async buildTreeNode(outline: Outline, parentPath: string): Promise<TreeNode> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return {
				children: [],
				className: "",
				data: undefined,
				draggable: false,
				droppable: false,
				icon: "",
				key: undefined,
				label: "",
				leaf: false,
				selectable: false,
				style: undefined
			};
		}

		try {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument!.get().id!
			);
			let documentSettings: DocumentSettings = portalDocument.getSettings();
			let children: Array<TreeNode> = [];
			let path: string = parentPath + PATH_SEPARATOR + outline.title;

			for (let child of outline.items) {
				children.push(await this.buildTreeNode(child, path));
				this.nodeCount++;
			}

			let pdfDocument: pdfjs.PDFDocumentProxy = await portalDocument.loadViewerData();
			let pdfPage: pdfjs.PDFPageProxy = await pdfDocument.getPage(documentSettings.activeViewer.currentPage);
			let pageRef: string = (pdfPage.ref as { num: number; gen: number; }).num + ' ' +
				(pdfPage.ref as { num: number; gen: number; }).gen;

			let outlineRef: string = "";
			if (FunctionHelper.exists(outline.dest?.length) && outline.dest![0] !== null) {
				outlineRef = outline.dest![0].num + ' ' + outline.dest![0].gen;
			}

			return {
				path: path,
				key: this.nodeCount,
				label: outline.title,
				data: outline,
				icon: "icon icon-bookmark",
				className: outlineRef === pageRef ? "active" : "",
				style: {
					fontWeight: outline.bold ? "bold" : "normal",
					fontStyle: outline.italic ? 'italic' : 'normal',
					color: "rgb(" + outline.color[0] + "," + outline.color[1] + "," + outline.color[2] + ")"
				},
				children: children
			} as TreeNode;
		} catch (e: any) {
			// can be ignored here
		}

		return {
			children: [],
			className: "",
			data: undefined,
			draggable: false,
			droppable: false,
			icon: "",
			key: undefined,
			label: "",
			leaf: false,
			selectable: false,
			style: undefined
		};
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<OutlineListProperties>, prevState: Readonly<OutlineListStatus>, snapshot?: any
	): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		if (this.props.portalDocument!.get().id !== prevProps.portalDocument?.get().id ||
			this.props.portalDocument!.get().updated !== prevProps.portalDocument?.get().updated ||
			this.props.portalDocument!.get().settings.viewerSettings.currentPage !==
			prevProps.portalDocument?.get().settings.viewerSettings.currentPage) {
			await this.buildOutlineList();
		}
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		await this.buildOutlineList();
	}

	/**
	 * This is called if a tree node is selected
	 */
	private handleSelect(event: TreeEventNodeEvent) {
		if (FunctionHelper.exists(this.props.destinationObserver)) {
			this.props.destinationObserver!.notify({dest: event.node.data.dest});
		}
	}

	/**
	 * Expands all Tree entries
	 */
	private expandList(): void {
		let expanded: TreeExpandedKeysType = {};

		for (let i: number = 1; i <= this.nodeCount; i++) {
			expanded[i] = true;
		}

		this.setState({
			expanded: expanded
		});
	}

	/**
	 * Collapses all Tree entries
	 */
	private collapseList(): void {
		this.setState({
			expanded: {}
		});
	}

	private checkPasswords(): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return false;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		if (portalDocument.hasPasswordProtection("permission") &&
			(!portalDocument.getDocumentFile()?.password?.isPermissionSet || portalDocument.hasError())
		) {
			return true;
		}

		return false;
	}

	private async deleteOutline(outlineParameter: Parameter<PortalToolboxOutlineOutline>): Promise<void> {
		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.deleteOutline(outlineParameter);
				}
			});

			return;
		}

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			outline: outlineParameter.data
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				let portalException: Exception = Exception.parseException(exception);
				if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
					await this.deleteOutline(outlineParameter);
				}
				return;
			}

			let message: Message = new Message("success.toolbox.outline.remove", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}

		this.setState({
			selected: undefined
		});
	}

	private findOutline(
		outlines: Array<TreeNode>, key: string | TreeMultipleSelectionKeys | TreeCheckboxSelectionKeys | null
	): TreeNode | null {
		for (let outline of outlines) {
			if (outline.key === this.state.selected) {
				return outline;
			}

			let result: TreeNode | null = null;
			if (outline.children) {
				result = this.findOutline(outline.children, key);

				if (result !== null) {
					return result;
				}
			}
		}

		return null;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();
		let outlineParameter: Parameter<PortalToolboxOutlineOutline> = DialogDefault.getParameter(
			PortalDialogType.REMOVE_OUTLINE, [portalDocument]
		);

		let selectionLocked: boolean = false;
		for (let selectedDocument of selection) {
			if (selectedDocument.isLocked()) {
				selectionLocked = true;
				break;
			}
		}

		return (
			<div className="body" style={this.props.visible ? {} : {display: "none"}}
				 data-help="global.sidebar.outlines" data-position="right">
				<div className="toolbar">
					<Button icon="icon icon-expand" onClick={this.expandList}
							tooltip={Translate.value("outline.expand", {ns: "sidebar"})}/>
					<Button icon="icon icon-collapse" onClick={this.collapseList}
							tooltip={Translate.value("outline.collapse", {ns: "sidebar"})}/>
					{
						FunctionHelper.applyValue((
							this.props.context.info.webserviceStatus?.webservices?.toolbox?.enabled &&
							this.props.context.info.functionsStatus?.outline
						), false) &&
						<div className="separator"/>
					}
					{(!this.props.documentChecks || OutlineAddDialogDefinition.isEnabled(this.props.context.info)) &&
						<Button icon="icon icon-add"
								tooltip={Translate.value("outline.add", {ns: "sidebar"})}
								disabled={
									selectionLocked ||
									(
										this.props.documentChecks &&
										!OutlineAddDialogDefinition.isActive(
											selection, this.props.context.info, this.props.signatureEditProfile
										)
									)
								}
								onClick={async () => {
									let command: () => Promise<void> = async () => {
										this.props.context.getPage().setState({
											showOutlineAddDialog: true,
											outlineAddParameter: undefined
										});
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}/>
					}
					{
						FunctionHelper.applyValue((
							this.props.context.info.webserviceStatus?.webservices?.toolbox?.enabled &&
							this.props.context.info.functionsStatus?.outline
						), false) &&
						<Button icon="icon icon-delete small"
								disabled={(() => {
									if (selectionLocked) {
										return true;
									}

									if (!PortalDialogSecurity.checkRestrictions(
										PortalDialogType.REMOVE_OUTLINE, this.props.signatureEditProfile,
										Intention.editingContent, portalDocument
									)) {
										return true;
									}

									return !FunctionHelper.exists(this.state.selected)
								})()}
								onClick={async () => {
									let command: () => Promise<void> = async () => {
										let outline: TreeNode | null =
											this.findOutline(this.state.outlines, this.state.selected!);
										if (outline === null) {
											return;
										}

										outlineParameter.data.remove!.item = [
											ItemReference.fromJson({
												path: (outline as any).path
											} as ItemReference)
										];

										await this.deleteOutline(outlineParameter);
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}
								tooltip={Translate.value("outline.remove", {ns: "sidebar"})}/>
					}
				</div>
				<div className="outline-list">
					{this.state.outlines.length > 0 &&
						<Tree value={this.state.outlines} selectionMode="single" selectionKeys={this.state.selected}
							  expandedKeys={this.state.expanded} propagateSelectionDown={false}
							  onSelectionChange={e => {
								  this.setState({selected: e.value})
							  }} onSelect={this.handleSelect}
							  onToggle={e => {
								  this.setState({expanded: e.value})
							  }}/>
					}
					{this.state.outlines.length === 0 &&
						<p className="no-results">
							<Translate {...this.props} data="outline.empty" namespace="sidebar"/>
						</p>
					}
				</div>
				<div className="toolbar inverted"/>
			</div>
		);
	}
}